import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const CartContext = createContext();

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export function CartProvider({ children }) {
  const [cartId, setCartId] = useState(null);
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize cart
  useEffect(() => {
    const initCart = async () => {
      const storedCartId = localStorage.getItem('cartId');
      if (storedCartId) {
        try {
          const response = await axios.get(`${API}/cart/${storedCartId}`);
          setCart(response.data);
          setCartId(storedCartId);
        } catch (e) {
          // Cart not found, create new one
          await createNewCart();
        }
      } else {
        await createNewCart();
      }
      setLoading(false);
    };

    initCart();
  }, []);

  const createNewCart = async () => {
    try {
      const response = await axios.post(`${API}/cart`);
      setCartId(response.data.id);
      setCart({ ...response.data, items: [], total: 0 });
      localStorage.setItem('cartId', response.data.id);
    } catch (e) {
      console.error('Error creating cart:', e);
    }
  };

  const refreshCart = useCallback(async () => {
    if (!cartId) return;
    try {
      const response = await axios.get(`${API}/cart/${cartId}`);
      setCart(response.data);
    } catch (e) {
      console.error('Error refreshing cart:', e);
    }
  }, [cartId]);

  const sizeQs = (size) => (size ? `&size=${encodeURIComponent(size)}` : '');

  const addToCart = async (productId, quantity = 1, size = null) => {
    if (!cartId) return;
    try {
      await axios.post(`${API}/cart/${cartId}/items`, {
        product_id: productId,
        quantity,
        size
      });
      await refreshCart();
      return true;
    } catch (e) {
      console.error('Error adding to cart:', e);
      return false;
    }
  };

  const updateQuantity = async (productId, quantity, size = null) => {
    if (!cartId) return;
    try {
      await axios.put(`${API}/cart/${cartId}/items/${productId}?quantity=${quantity}${sizeQs(size)}`);
      await refreshCart();
    } catch (e) {
      console.error('Error updating cart:', e);
    }
  };

  const removeFromCart = async (productId, size = null) => {
    if (!cartId) return;
    try {
      const qs = size ? `?size=${encodeURIComponent(size)}` : '';
      await axios.delete(`${API}/cart/${cartId}/items/${productId}${qs}`);
      await refreshCart();
    } catch (e) {
      console.error('Error removing from cart:', e);
    }
  };

  const clearCart = async () => {
    await createNewCart();
  };

  const cartItemsCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <CartContext.Provider value={{
      cartId,
      cart,
      loading,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      refreshCart,
      cartItemsCount
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
